/* eslint-disable no-case-declarations */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './SchedulerContainer.module.scss';
import { schedulerActions } from '../../store';
import dayjs from 'dayjs';
import { findChairById } from '../CalendarHeader/components/CalendarCustomizeContent/sameChairId';
import { getCalendarLayoutSource, getCalendarLoaded, getCalendarLoading } from '../../store/selectors';
import SchedulerDay from '../SchedulerDay';
import successSound from '../../../../assets/sounds/1.mp3';
import failSound from '../../../../assets/sounds/2.mp3';

import { getActiveSalonId, getActiveSalon } from '../../../Auth/store/selectors';
import { useSearchParams } from 'react-router-dom';
import CalendarHeader from '../CalendarHeader';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import SchedulerAddTaskAndQueueButtons from '../SchedulerAddTaskAndQueueButtons';
import Spinner from '../../../Common/Spinner';
import { useSSE } from '../../../../utils/useSSE';
import { toast } from 'react-toastify';
import { formatCalendarData } from '../utils/formatCalendarData';
import {
  COMPACT_CLOSED_DAYS_PARAM,
  HIDE_CLOSED_CHAIRS_PARAM,
  getClosureApiFlags,
  isClosureFilterOn,
} from '../utils/closureHelpers';
import ClosedDayStrip from '../ClosedDayStrip';
import { ToastSuccess } from '../Toast';
import store from 'store/store';
// import { Crisp } from 'crisp-sdk-web';
import { modalActions } from 'Containers/Modals/store';
import { getActiveSalonePaymentInfo } from 'utils/salonToken';
import { getMyAccountStaff } from 'Containers/Settings/store/selectors';
import { settingsActions } from 'Containers/Settings/store';
import { useTranslation } from 'react-i18next';
import { CalendarOff, X } from 'lucide-react';
import { emitSchedulerResetGestures } from '../utils/mobileTaskGestures';
import {
  MAJOR_TICK_MINUTES,
  MINOR_TICK_MINUTES,
  TIME_AXIS_WIDTH_MOBILE,
  CALENDAR_PARAMS_STORAGE_KEY,
  formatClockLabel,
  getCalendarSlotCount,
  getCalendarTicks,
  getCalendarZoomScale,
  getFittedCardSpace,
  getInitialStepSize,
  getSchedulerInnerHeight,
  getTimeLabelOffset,
  getTimeLabelTop,
  getVisibleEndWorkTime,
  getWorkerHeaderOffset,
  getZoomedCardSpace,
} from '../../constants/layout';
import useMobileAxisLock from './useMobileAxisLock';
import useForcedTouchScroll from './useForcedTouchScroll';

const failSoundUrl = failSound;
const successSoundUrl = successSound;
let failAudio;
let successAudio;

const getFailAudio = () => {
  if (!failAudio) {
    failAudio = new Audio(failSoundUrl);
    failAudio.volume = 0.2;
  }
  return failAudio;
};

const getSuccessAudio = () => {
  if (!successAudio) {
    successAudio = new Audio(successSoundUrl);
    successAudio.volume = 0.2;
  }
  return successAudio;
};
const REFRESH_COOLDOWN_MS = 2 * 60 * 1000;
/** Se getCalendario non risponde (WebView/iPad), mostra Riprova invece dello spinner infinito. */
const CALENDAR_LOAD_STALL_MS = 15000;

const DEFAULT_SEARCH_PARAMS = {
  startDay: () => dayjs().format('YYYY-MM-DD'),
  numberOfActiveDays: '1',
  activeWorkerId: '0',
  reset: '0',
};

const readStoredSearchParams = () => {
  try {
    const raw = localStorage.getItem('searchParams');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/** Garantisce startDay / numberOfActiveDays validi (localStorage incompleto = spinner infinito). */
const normalizeCalendarSearchParams = (source = {}) => {
  const startRaw = source.startDay;
  const startDay =
    startRaw && dayjs(startRaw).isValid()
      ? dayjs(startRaw).format('YYYY-MM-DD')
      : DEFAULT_SEARCH_PARAMS.startDay();
  const days = Number(source.numberOfActiveDays);
  const numberOfActiveDays = Number.isFinite(days) && days > 0 ? String(Math.round(days)) : '1';
  const activeWorkerId =
    source.activeWorkerId != null && source.activeWorkerId !== ''
      ? String(source.activeWorkerId)
      : '0';

  return {
    ...source,
    startDay,
    numberOfActiveDays,
    activeWorkerId,
    reset: source.reset != null ? String(source.reset) : '0',
  };
};

const resolveStaffChairId = (poltrona) => {
  if (poltrona == null || poltrona === '') return null;
  if (typeof poltrona === 'object') {
    return poltrona.idpoltrona ?? poltrona.id ?? null;
  }
  return poltrona;
};

const applyTaskSearch = (days, query) => {
  if (!Array.isArray(days)) return [];
  const normalized = (query || '').trim().toLowerCase();

  return days.map((day) => ({
    ...day,
    workers: (day.workers || []).map((worker) => ({
      ...worker,
      workerTasks: (worker.workerTasks || []).map((task) => {
        if (task.limitCard) return task;
        const completeName = `${task.nome || ''} ${task.cognome || ''}`.trim().toLowerCase();
        const serviceMatch = (task.servizi || []).some((service) =>
          (service.nome || '').toLowerCase().includes(normalized),
        );
        const match = !normalized || completeName.includes(normalized) || serviceMatch;
        return {
          ...task,
          matched: match,
          opacity: match ? 1 : 0.2,
        };
      }),
    })),
  }));
};

const SchedulerContainer = () => {
  const activeSalon = useSelector(getActiveSalon());
  const payment_disable = getActiveSalonePaymentInfo();
  const dispatch = useDispatch();
  const localSearchData = readStoredSearchParams();
  const localCalendarData = localStorage.getItem(CALENDAR_PARAMS_STORAGE_KEY);

  let localCalendarParams = {};
  try {
    localCalendarParams = localCalendarData ? JSON.parse(localCalendarData) || {} : {};
  } catch {
    localCalendarParams = {};
  }

  const initialSearchParams = normalizeCalendarSearchParams(
    localSearchData || {
      startDay: DEFAULT_SEARCH_PARAMS.startDay(),
      numberOfActiveDays: DEFAULT_SEARCH_PARAMS.numberOfActiveDays,
      activeWorkerId: DEFAULT_SEARCH_PARAMS.activeWorkerId,
      reset: DEFAULT_SEARCH_PARAMS.reset,
    },
  );
  const { isMobile, isTablet } = useWindowSize();
  const isTouchLayout = isMobile || isTablet;
  const initialCalendarParams = {
    stepSize: isMobile ? 8 : 14,
    startWorkTime: { hours: 7, minutes: 0 },
    endWorkTime: { hours: 23, minutes: 0 },
    ...localCalendarParams,
  };

  const [startWorkTime, setStartWorkTime] = useState(initialCalendarParams.startWorkTime);
  const [endWorkTime, setEndWorkTime] = useState(initialCalendarParams.endWorkTime);
  const [timeStep, setTimeStep] = useState({ hours: 0, minutes: 5 });
  const [cardSpace, setCardSpace] = useState(() =>
    getZoomedCardSpace(getInitialStepSize(localCalendarParams.stepSize, isMobile), { isMobile }),
  );

  const [stepSize, setStepSize] = useState(() =>
    getInitialStepSize(localCalendarParams.stepSize, isMobile),
  );
  const zoomScale = getCalendarZoomScale(stepSize, isMobile);
  const visibleEndWorkTime = getVisibleEndWorkTime(endWorkTime);
  const [startData, setStartDataState] = useState({});
  const startDataRef = useRef({});
  const setStartData = useCallback((next) => {
    startDataRef.current = next || {};
    setStartDataState(next || {});
  }, []);
  const [activeDays, setActiveDays] = useState([]);
  const [searchString, setSearchString] = useState('');
  const layoutSource = useSelector(getCalendarLayoutSource);
  const calendarLoading = useSelector(getCalendarLoading());
  const calendarLoaded = useSelector(getCalendarLoaded());
  const activeSalonId = useSelector(getActiveSalonId());
  const [searchParams, setSearchParams] = useSearchParams(initialSearchParams);
  const [loadStalled, setLoadStalled] = useState(false);
  const startDay =
    searchParams.get('startDay') && dayjs(searchParams.get('startDay')).isValid()
      ? dayjs(searchParams.get('startDay')).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD');
  const parsedActiveDays = Number(searchParams.get('numberOfActiveDays'));
  const numberOfActiveDays =
    Number.isFinite(parsedActiveDays) && parsedActiveDays > 0 ? Math.round(parsedActiveDays) : 1;
  const showHeader = numberOfActiveDays > 1;
  const activeWorker = useMemo(
    () => findChairById(layoutSource.workers, searchParams.get('activeWorkerId')),
    [layoutSource.workers, searchParams],
  );
  const container_ref = useRef(null);
  const shownBookingToastsRef = useRef(new Set());
  const [openAutomaticModal, setAutomaticModal] = useState(false);
  const [closedDayBannerDismissed, setClosedDayBannerDismissed] = useState(false);
  const [expandedClosedDays, setExpandedClosedDays] = useState(() => new Set());
  const [expandedClosedChairs, setExpandedClosedChairs] = useState(() => new Set());

  const myAccount = useSelector(getMyAccountStaff());
  const { t } = useTranslation();

  const hideClosedChairs = isClosureFilterOn(searchParams, HIDE_CLOSED_CHAIRS_PARAM);
  const compactClosedDays = isClosureFilterOn(searchParams, COMPACT_CLOSED_DAYS_PARAM);

  const activeWorkers = activeWorker ? [activeWorker] : layoutSource.workers;

  const calendarItemsCount = getCalendarSlotCount(startWorkTime, visibleEndWorkTime) || 1;
  const timeLabelOffset = getTimeLabelOffset(showHeader, zoomScale);
  const workerHeaderOffset = getWorkerHeaderOffset(showHeader, zoomScale);
  const majorTimeTicks = getCalendarTicks(startWorkTime, visibleEndWorkTime, MAJOR_TICK_MINUTES, {
    includeEnd: true,
  });
  const minorTimeTicks = getCalendarTicks(
    startWorkTime,
    visibleEndWorkTime,
    MINOR_TICK_MINUTES,
  ).filter((tick) => tick % MAJOR_TICK_MINUTES !== 0);

  useEffect(() => {
    if(localStorage.getItem('staff') && !myAccount) {
      dispatch(settingsActions.GET_MY_ACCOUNT_STAFF.REQUEST());
    }
  }, []);


  useEffect(() => {
    if (!localStorage.getItem('staff') || !myAccount) return;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const permessi = myAccount?.permessi?.length > 0 ? myAccount.permessi : [];
      const calendar = permessi.find((perm) => perm.sezione === 1) || null;
      if (calendar) {
        if (calendar.viewJustMe == 1) {
          next.set('viewJustMe', 1);
          const chairId = resolveStaffChairId(myAccount.poltrona);
          if (chairId != null) next.set('activeWorkerId', String(chairId));
        } else next.set('viewJustMe', 0);

        if (calendar.allowInsert == 1) {
          next.set('allowInsert', 1);
        } else next.set('allowInsert', 0);

        if (calendar.allowEdit == 1) {
          next.set('allowEdit', 1);
        } else next.set('allowEdit', 0);
      }

      const client = permessi.find((perm) => perm.sezione === 3) || null;
      if (client) {
        next.set('client', 1);
      } else next.set('client', 0);

      const salon_management = permessi.find((perm) => perm.sezione === 2) || null;
      if (salon_management) {
        next.set('salon_management', 1);
      } else next.set('salon_management', 0);

      return next;
    });
  }, [myAccount]);

  // Ripara URL/localStorage incompleti (es. numberOfActiveDays assente → spinner infinito su iPad).
  useEffect(() => {
    const rawDays = searchParams.get('numberOfActiveDays');
    const rawStart = searchParams.get('startDay');
    const daysOk = Number(rawDays) > 0;
    const startOk = rawStart && dayjs(rawStart).isValid();
    if (daysOk && startOk) return;

    setSearchParams((prev) => {
      const normalized = normalizeCalendarSearchParams(Object.fromEntries(prev));
      const next = new URLSearchParams(prev);
      next.set('startDay', normalized.startDay);
      next.set('numberOfActiveDays', normalized.numberOfActiveDays);
      if (!prev.get('activeWorkerId')) next.set('activeWorkerId', normalized.activeWorkerId);
      try {
        localStorage.setItem('searchParams', JSON.stringify(Object.fromEntries(next)));
      } catch {
        /* private mode */
      }
      return next;
    });
  }, [searchParams, setSearchParams]);


  //get permessi: 
  // calendar: (filter poltrone | insert modal | edit modal | drug task)
  //   - se permessi.length > 0 e localStorage.getItem('staff') e permessi.find(perm => perm.sezione === 1) : calendar
  //   - calendar.viewJustMe==1 | calendar.allowInsert==1 | calendar.allowEdit==1
  // salon_management: (gestione poltrona btn)
  //   - se permessi.length > 0 e localStorage.getItem('staff') e permessi.find(perm => perm.sezione === 2) : salon_management
  // client: (dettaglio cliente update api)
  //   - se permessi.length > 0 e localStorage.getItem('staff') e permessi.find(perm => perm.sezione === 3) : client


  useEffect(() => {
    let previous = {};
    try {
      previous = JSON.parse(localStorage.getItem(CALENDAR_PARAMS_STORAGE_KEY) || '{}') || {};
    } catch (e) {
      previous = {};
    }
    localStorage.setItem(
      CALENDAR_PARAMS_STORAGE_KEY,
      JSON.stringify({ ...previous, startWorkTime, endWorkTime, stepSize }),
    );
  }, [startWorkTime, endWorkTime, stepSize]);

  const [lastRefresh, setLastRefresh] = useState(0);
  const [refreshTimeElapsed, setRefreshTimeElapsed] = useState(t('scheduler.time.now'));
  const [refreshDisabledUntil, setRefreshDisabledUntil] = useState(null);
  const [staleNeedsRefresh, setStaleNeedsRefresh] = useState(false);
  const hiddenAtRef = useRef(null);
  const refreshDisabledUntilRef = useRef(null);
  refreshDisabledUntilRef.current = refreshDisabledUntil;

  const startRefreshCooldown = () => {
    setRefreshDisabledUntil(Date.now() + REFRESH_COOLDOWN_MS);
  };

  const canRefreshCalendar = !refreshDisabledUntil || Date.now() >= refreshDisabledUntil;

  useEffect(() => {
    if (!refreshDisabledUntil) return;

    const remaining = refreshDisabledUntil - Date.now();
    if (remaining <= 0) {
      setRefreshDisabledUntil(null);
      return;
    }

    const timer = window.setTimeout(() => setRefreshDisabledUntil(null), remaining);
    return () => window.clearTimeout(timer);
  }, [refreshDisabledUntil]);

  const getCalendar = () => {
    dispatch(
      schedulerActions.GET_CALENDAR_DATA.REQUEST(
        {
          startDate: dayjs(startDay).format('YYYY-MM-DD'),
          endDate: dayjs(startDay).add(numberOfActiveDays, 'day').format('YYYY-MM-DD'),
          ...getClosureApiFlags(searchParams),
        },
        null,
        { skipLoader: true },
      ),
    );
    numberOfActiveDays === 1 &&
      dispatch(
        schedulerActions.GET_CALENDAR_QUEUE.REQUEST(
          {
            date: dayjs(startDay).format('YYYY-MM-DD'),
          },
          null,
          { skipLoader: true },
        ),
      );
    setActiveDays(
      Array.from({ length: numberOfActiveDays }, (_, index) =>
        dayjs(startDay).add(index, 'day').format('YYYY-MM-DD'),
      ),
    );
    setLastRefresh(0);
    setRefreshTimeElapsed(t('scheduler.time.now'));
  };

  const getCalendarRef = useRef(getCalendar);
  getCalendarRef.current = getCalendar;
  const reconnectSseRef = useRef(null);
  const softRefreshRef = useRef(null);

  const softRefreshCalendar = ({ scrollTop = false, respectCooldown = true } = {}) => {
    if (respectCooldown) {
      const until = refreshDisabledUntilRef.current;
      if (until && Date.now() < until) {
        reconnectSseRef.current?.();
        return false;
      }
    }
    startRefreshCooldown();
    getCalendarRef.current();
    reconnectSseRef.current?.();
    if (scrollTop && container_ref.current) {
      container_ref.current.scrollTo(0, 0);
    }
    setStaleNeedsRefresh(false);
    return true;
  };
  softRefreshRef.current = softRefreshCalendar;

  const refreshCalendar = () => {
    softRefreshCalendar({ scrollTop: true, respectCooldown: true });
  };

  const getLastRefreshTime = () => {
    const increment = lastRefresh + 1;
    setLastRefresh(increment);
    let minutes = lastRefresh;
    let hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
    hours = hours % 24;

    if (minutes > 0 || hours > 0) {
      setRefreshTimeElapsed(
        `${hours > 0 ? `${hours} ${hours > 1 ? t('scheduler.time.hours') : t('scheduler.time.hour')}` : ''} ${minutes} ${
          minutes > 1 ? t('scheduler.time.minutes') : t('scheduler.time.minute')
        } ${t('scheduler.time.ago')}`,
      );
    }
  };

  useEffect(() => {
    const refreshTimeInterval = setInterval(() => {
      getLastRefreshTime();
    }, 60000);
    return () => clearInterval(refreshTimeInterval);
  }, [lastRefresh]);

  useEffect(() => {
    if (isMobile) return;
    setCardSpace(getZoomedCardSpace(stepSize, { isMobile: false }));
  }, [isMobile, stepSize]);
  const workersWorkload = useMemo(
    () =>
      applyTaskSearch(
        formatCalendarData({
          appointments: layoutSource.appointments,
          activeWorkers,
          activeDays,
          startWorkTime,
          endWorkTime: visibleEndWorkTime,
          calendarItemsCount,
          timeStep,
          stepSize,
          services: layoutSource.services,
        }),
        searchString,
      ),
    [
      layoutSource,
      activeWorkers,
      activeDays,
      timeStep,
      stepSize,
      calendarItemsCount,
      visibleEndWorkTime,
      startWorkTime,
      searchString,
    ],
  );

  const displayWorkload = useMemo(() => {
    if (!workersWorkload.length) {
      return { days: [], singleDayFullyClosed: null };
    }

    if (numberOfActiveDays === 1 && workersWorkload[0]?.fullyClosed) {
      return {
        days: workersWorkload.map((day) => ({
          ...day,
          compactClosed: false,
          workers: day.workers.map((worker) => ({
            ...worker,
            chairCompact: false,
          })),
        })),
        singleDayFullyClosed: workersWorkload[0],
      };
    }

    const annotateWorker = (day, worker) => {
      const chairKey = `${day.date}:${worker.id}`;
      const chairCompact =
        hideClosedChairs &&
        !!worker.fullyClosed &&
        !expandedClosedChairs.has(chairKey);

      return {
        ...worker,
        chairCompact,
      };
    };

    const days = workersWorkload.map((day) => {
      const isExpandedClosed = expandedClosedDays.has(day.date);

      if (
        numberOfActiveDays >= 3 &&
        day.fullyClosed &&
        !isExpandedClosed &&
        (compactClosedDays || hideClosedChairs)
      ) {
        return { ...day, compactClosed: true };
      }

      return {
        ...day,
        compactClosed: false,
        workers: day.workers.map((worker) => annotateWorker(day, worker)),
      };
    });

    return { days, singleDayFullyClosed: null };
  }, [
    workersWorkload,
    numberOfActiveDays,
    hideClosedChairs,
    compactClosedDays,
    expandedClosedDays,
    expandedClosedChairs,
  ]);

  useMobileAxisLock(container_ref, isTouchLayout && displayWorkload.days.length > 0);
  useForcedTouchScroll(container_ref, isTablet && displayWorkload.days.length > 0);

  useLayoutEffect(() => {
    if (!isMobile) return undefined;
    const el = container_ref.current;
    if (!el) return undefined;

    const update = () => {
      const day = displayWorkload.days.find((item) => !item.compactClosed);
      const chairs = (day?.workers || []).filter((worker) => !worker.chairCompact).length;
      const fitted = getFittedCardSpace(el.clientWidth, chairs, {
        timeAxisWidth: TIME_AXIS_WIDTH_MOBILE,
      });
      setCardSpace(getZoomedCardSpace(stepSize, { isMobile: true, fitted }));
    };

    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, displayWorkload, stepSize]);

  const showClosedDayBanner =
    !!displayWorkload.singleDayFullyClosed && !closedDayBannerDismissed;

  useEffect(() => {
    setClosedDayBannerDismissed(false);
  }, [displayWorkload.singleDayFullyClosed?.date]);

  useEffect(() => {
    setExpandedClosedDays(new Set());
    setExpandedClosedChairs(new Set());
  }, [startDay, numberOfActiveDays]);

  // Riattivare i toggle deve richudere giorni/poltrone espansi manualmente
  useEffect(() => {
    if (compactClosedDays) setExpandedClosedDays(new Set());
  }, [compactClosedDays]);

  useEffect(() => {
    if (hideClosedChairs) {
      setExpandedClosedChairs(new Set());
      setExpandedClosedDays(new Set());
    }
  }, [hideClosedChairs]);

  const dismissClosedDayBanner = () => setClosedDayBannerDismissed(true);

  const expandClosedDay = (date) => {
    setExpandedClosedDays((prev) => {
      if (prev.has(date)) return prev;
      const next = new Set(prev);
      next.add(date);
      return next;
    });
  };

  const expandClosedChair = (date, workerId) => {
    const key = `${date}:${workerId}`;
    setExpandedClosedChairs((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const openClosuresSettings = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'calendarSettingsModal',
        props: {
          startDay,
          daySettings: numberOfActiveDays === 1 && dayjs().isSameOrBefore(startDay, 'day'),
          searchParams,
        },
      }),
    );
  };

  const callbackSSE = (data) => {
    const toastVariants = {
      prenotazione: {
        className: 'toast-booking-new',
        progressClassName: 'toast-booking-new_progress',
      },
      modifica: {
        className: 'toast-booking-update',
        progressClassName: 'toast-booking-update_progress',
      },
      disdetta: {
        className: 'toast-warning',
        progressClassName: 'toast-warning_progress',
      },
    };
    const variant = toastVariants[data.type] || toastVariants.prenotazione;
    const bookingId = data.event?.idprenotazione;
    const toastKey = bookingId ? `${data.type}-${bookingId}` : null;
    const toastConfig = {
      autoClose: data.type === 'prenotazione' ? 5000 : 4000,
      className: variant.className,
      hideProgressBar: false,
      progressClassName: variant.progressClassName,
      closeOnClick: true,
      theme: 'light',
      icon: false,
    };
    var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const appointments = store.getState().schedulerReducer.calendar.appointments;
    const existingAppointment = Object.values(appointments)
      .flatMap((day) =>
        day.find(({ idprenotazione }) => idprenotazione === data.event.idprenotazione),
      )
      .filter(Boolean)[0];
    const currentTimestamp = new Date().getTime();

    switch (data.type) {
      case 'prenotazione':
        if (toastKey && shownBookingToastsRef.current.has(toastKey)) return;
        if (existingAppointment?.isNotNew) return;
        if (toastKey) shownBookingToastsRef.current.add(toastKey);
        if (!isSafari) getSuccessAudio().play();
        toast(<ToastSuccess booking={data} />, toastConfig);
        return dispatch(
          schedulerActions.ADD_CALENDAR_TASK.SUCCESS([{ ...data.event, isNotNew: true }]),
        );
      case 'disdetta':
        if (!isSafari) getFailAudio().play();
        toast(
          <ToastSuccess
            booking={{
              ...data,
              event: { ...(existingAppointment || {}), ...(data.event || {}) },
            }}
            type='delete'
          />,
          toastConfig,
        );
        return dispatch(
          schedulerActions.DELETE_CALENDAR_TASK.SUCCESS({
            id: data.event.idprenotazione,
            all: data.event.deleteAllRicorrenti,
            ricorrente: data.event?.ricorrente,
          }),
        );
      case 'modifica':
        if (!isSafari) getSuccessAudio().play();
        toast(
          <ToastSuccess
            booking={{
              ...data,
              event: { ...(existingAppointment || {}), ...(data.event || {}) },
            }}
            type='update'
          />,
          toastConfig,
        );
        return dispatch(schedulerActions.EDIT_CALENDAR_TASK.SUCCESS(data.event));
    }
  };

  useEffect(() => {
    setAutomaticModal(false);
  }, []);

  const { reconnect: reconnectSSE } = useSSE(callbackSSE, { pathIncludes: 'scheduler' });
  reconnectSseRef.current = reconnectSSE;

  useEffect(() => {
    const RESUME_SOFT_MS = 30 * 1000;
    const RESUME_SSE_MS = 2 * 60 * 1000;
    const RESUME_STALE_MS = 10 * 60 * 1000;

    const resetGestures = () => {
      emitSchedulerResetGestures();
    };

    const handleResume = () => {
      // Sempre sblocca gesture prima di qualsiasi refresh
      resetGestures();

      const hiddenAt = hiddenAtRef.current;
      if (!hiddenAt) return;
      const elapsed = Date.now() - hiddenAt;
      hiddenAtRef.current = null;

      if (elapsed <= RESUME_SOFT_MS) return;

      softRefreshRef.current({ scrollTop: false, respectCooldown: true });

      if (elapsed > RESUME_SSE_MS) {
        reconnectSseRef.current?.();
      }

      if (elapsed > RESUME_STALE_MS) {
        setStaleNeedsRefresh(true);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        // Unlock immediato: non lasciare scroll-lock / touchmove in sospeso
        resetGestures();
        return;
      }
      if (document.visibilityState === 'visible') {
        handleResume();
      }
    };

    const onPageShow = () => {
      if (document.visibilityState === 'visible') {
        handleResume();
      }
    };

    const onPageHide = () => {
      hiddenAtRef.current = Date.now();
      resetGestures();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  useEffect(() => {
    if( activeSalon && (!activeSalon.titolare_iban || !activeSalon.iban) && !payment_disable && openAutomaticModal) {
      setAutomaticModal(false);
      dispatch(
        modalActions.OPEN_MODAL.SUCCESS({
          type: 'editSalonIbanModal',
          props: { 
            activeSalon: activeSalon, 
            title: t('scheduler.modal.payNow.title'), 
            subtitle: t('scheduler.modal.payNow.subtitle') },
        })
      );
    }
  }, [openAutomaticModal]);

  useEffect(() => {
    if (searchParams.get('reset') === '1' && container_ref.current) {
      container_ref.current.scrollTo(0, 0);
      searchParams.set('reset', '0');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!numberOfActiveDays || !activeSalonId || !startDay) return;
    startRefreshCooldown();
    getCalendar();
  }, [startDay, numberOfActiveDays, activeSalonId]);

  const isCalendarPending =
    (calendarLoading && !workersWorkload.length) ||
    (!calendarLoaded && !displayWorkload.days.length);

  useEffect(() => {
    if (!isCalendarPending) {
      setLoadStalled(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setLoadStalled(true), CALENDAR_LOAD_STALL_MS);
    return () => window.clearTimeout(timer);
  }, [isCalendarPending, startDay, numberOfActiveDays, activeSalonId]);

  const retryCalendarLoad = () => {
    setLoadStalled(false);
    if (!activeSalonId) return;
    softRefreshCalendar({ scrollTop: true, respectCooldown: false });
  };
  return (
    <>
      <CalendarHeader
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        startDay={startDay}
        numberOfActiveDays={numberOfActiveDays}
        activeDays={activeDays}
        searchString={searchString}
        setSearchString={setSearchString}
        activeWorker={activeWorker}
        setStepSize={setStepSize}
        stepSize={stepSize}
        workTime={{ setEndWorkTime, setStartWorkTime, startWorkTime, endWorkTime }}
        giorniVisibiliSalonePassato={+activeSalon?.giorniVisibiliSalonePassato || 0}
        onRefreshCalendar={isMobile ? refreshCalendar : undefined}
        canRefreshCalendar={canRefreshCalendar}
        refreshHighlighted={isMobile && staleNeedsRefresh}
        onRefreshHighlightClear={() => setStaleNeedsRefresh(false)}
      />

      {/* {!isMobile && (
        <div
          style={{
            fontSize: '14px',
            fontWeight: '400',
            padding: '15px 20px',
            marginTop: '5px',
            background: '#FCB734',
            width: 'fit-content',
          }}
        >
          {t('scheduler.refreshBanner.title')} <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f1f1f' }}>
            {refreshTimeElapsed}
          </span>
        </div>
      )} */}

      <div
        className={clx(styles.scheduler__wrapper, {
          [styles.scheduler__wrapper__mobile]: isTouchLayout,
        })}
      >
        {calendarLoading && !workersWorkload.length ? (
          <div className={styles.scheduler_state}>
            {loadStalled ? (
              <>
                <p>{t('scheduler.grid.loadFailed')}</p>
                <button type="button" className={styles.scheduler_retry} onClick={retryCalendarLoad}>
                  {t('scheduler.grid.retry')}
                </button>
              </>
            ) : (
              <>
                <Spinner inline />
                <p>{t('scheduler.grid.loading')}</p>
              </>
            )}
          </div>
        ) : displayWorkload.days.length ? (
          <div
            className={clx(styles.scheduler_grid_shell, {
              [styles.scheduler_grid_shell_loading]: calendarLoading,
              [styles.scheduler_grid_shell_closed]: showClosedDayBanner,
            })}
            aria-busy={calendarLoading}
          >
            {showClosedDayBanner ? (
              <div className={styles.scheduler_closed_day_overlay}>
                <div
                  className={styles.scheduler_closed_day_panel}
                  role="dialog"
                  aria-labelledby="closed-day-title"
                >
                  <button
                    type="button"
                    className={styles.scheduler_closed_day_close}
                    onClick={dismissClosedDayBanner}
                    aria-label={t('common.actions.close')}
                  >
                    <X size={18} strokeWidth={2.4} />
                  </button>
                  <div className={styles.scheduler_closed_day_icon} aria-hidden>
                    <CalendarOff size={isMobile ? 20 : 28} strokeWidth={2} />
                  </div>
                  <p className={styles.scheduler_closed_day_eyebrow}>
                    {t('scheduler.closures.closed')}
                  </p>
                  <h2 id="closed-day-title" className={styles.scheduler_closed_day_title}>
                    {t('scheduler.closures.closedDayTitle')}
                  </h2>
                  <p className={styles.scheduler_closed_day_date}>
                    {dayjs(displayWorkload.singleDayFullyClosed.date).format('dddd D MMMM YYYY')}
                  </p>
                  <p className={styles.scheduler_closed_day_copy}>
                    {t('scheduler.closures.closedDaySubtitle')}
                  </p>
                  <div className={styles.scheduler_closed_day_actions}>
                    {(searchParams.get('allowInsert') == 1 || !localStorage.getItem('staff')) && (
                      <button
                        type="button"
                        className={styles.scheduler_closed_day_cta}
                        onClick={dismissClosedDayBanner}
                      >
                        {t('scheduler.closures.bookAnyway')}
                      </button>
                    )}
                    {(searchParams.get('allowInsert') == 1 || !localStorage.getItem('staff')) && (
                      <button
                        type="button"
                        className={styles.scheduler_closed_day_secondary}
                        onClick={openClosuresSettings}
                      >
                        {t('scheduler.closures.manageClosures')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
            <div className={styles.scheduler__container} ref={container_ref}>
              <div
                className={styles.scheduler__inner}
                style={{
                  height: `${getSchedulerInnerHeight(calendarItemsCount, stepSize, showHeader, zoomScale)}px`,
                  '--calendar-zoom': zoomScale,
                }}
              >
                <div className={styles.scheduler_time_line_container}>
                  <div
                    className={styles.time_line_corner}
                    style={{
                      height: `${workerHeaderOffset}px`,
                    }}
                  />
                  {majorTimeTicks.map((tick, index) => {
                    const isFirst = index === 0;
                    const isLast = index === majorTimeTicks.length - 1;
                    return (
                      <div
                        key={`major-${tick}`}
                        className={styles.time_line_value_container}
                        style={{
                          top: `${getTimeLabelTop(tick, startWorkTime, stepSize, timeLabelOffset, {
                            isFirst,
                            isLast,
                          })}px`,
                        }}
                      >
                        <div className={styles.time_line_value}>{formatClockLabel(tick)}</div>
                      </div>
                    );
                  })}
                  {!isMobile
                    ? minorTimeTicks.map((tick) => (
                        <div
                          key={`minor-${tick}`}
                          className={styles.time_line_value_container_half}
                          style={{
                            top: `${getTimeLabelTop(tick, startWorkTime, stepSize, timeLabelOffset)}px`,
                          }}
                        >
                          <div className={styles.time_line_value}>{formatClockLabel(tick)}</div>
                        </div>
                      ))
                    : null}
                </div>
                <div className={styles.scheduler_tasks_container}>
                  {displayWorkload.days.map((day) => {
                    if (day.compactClosed) {
                      return (
                        <ClosedDayStrip
                          key={day.date}
                          day={day}
                          showHeader={showHeader}
                          onExpand={() => expandClosedDay(day.date)}
                        />
                      );
                    }
                    const timeStep = day.workTime / calendarItemsCount;
                    return (
                      <SchedulerDay
                        setStartData={setStartData}
                        startDataRef={startDataRef}
                        key={day.date}
                        cardSpace={cardSpace}
                        calendarItemsCount={calendarItemsCount}
                        timeStep={timeStep}
                        stepSize={stepSize}
                        startWorkTime={startWorkTime}
                        endWorkTime={visibleEndWorkTime}
                        day={day}
                        startData={startData}
                        numberOfActiveDays={numberOfActiveDays}
                        containerRef={container_ref}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        onExpandClosedChair={expandClosedChair}
                        zoomScale={zoomScale}
                      />
                    );
                  })}
                </div>
              </div>
              {(isMobile ||
                (!showClosedDayBanner &&
                  (searchParams.get('allowInsert') == 1 || !localStorage.getItem('staff')))) && (
                <SchedulerAddTaskAndQueueButtons
                  numberOfActiveDays={numberOfActiveDays}
                  showAddActions={
                    !showClosedDayBanner &&
                    (searchParams.get('allowInsert') == 1 || !localStorage.getItem('staff'))
                  }
                />
              )}
            </div>
          </div>
        ) : calendarLoaded ? (
          <div className={styles.scheduler_state}>
            <p>{t('scheduler.grid.empty')}</p>
          </div>
        ) : (
          <div className={styles.scheduler_state}>
            {loadStalled ? (
              <>
                <p>{t('scheduler.grid.loadFailed')}</p>
                <button type="button" className={styles.scheduler_retry} onClick={retryCalendarLoad}>
                  {t('scheduler.grid.retry')}
                </button>
              </>
            ) : (
              <>
                <Spinner inline />
                <p>{t('scheduler.grid.loading')}</p>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SchedulerContainer;
